// useSavedPlaybooks — bookmark playbooks to localStorage so the user can
// pin favorites and reopen the inline runner later without re-searching.
//
// Why localStorage (not Supabase): the home runner is anonymous-friendly.
// Bookmarks are a per-device convenience, not data we need to share across
// devices, so we keep it client-side (zero auth friction, zero backend cost).
// Distinct from `usePlaybookProgress`, which only tracks the SINGLE most
// recent in-progress session. Saved bookmarks are an explicit, multi-item
// list the user curates.
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "hl.playbook.saved.v1";
const MAX_SAVED = 24; // generous cap to keep localStorage tiny

export type SavedPlaybook = {
  slug: string;
  /** Optional: the topic the user was exploring when they saved. */
  topic: string;
  savedAt: number;
};

function read(): SavedPlaybook[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is SavedPlaybook =>
        r && typeof r.slug === "string" && typeof r.savedAt === "number",
    );
  } catch {
    return [];
  }
}

function write(rows: SavedPlaybook[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    /* quota / privacy mode — silently no-op */
  }
}

export function useSavedPlaybooks() {
  const [saved, setSaved] = useState<SavedPlaybook[]>(() => read());

  // Cross-tab sync: if the user pins a playbook in another tab, mirror it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSaved(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isSaved = useCallback(
    (slug: string) => saved.some((r) => r.slug === slug),
    [saved],
  );

  const save = useCallback((slug: string, topic = "") => {
    setSaved((prev) => {
      // Move-to-front if already present so the most recently-touched
      // bookmark wins the prime real estate in the hero list.
      const without = prev.filter((r) => r.slug !== slug);
      const next = [{ slug, topic: topic.trim(), savedAt: Date.now() }, ...without].slice(
        0,
        MAX_SAVED,
      );
      write(next);
      return next;
    });
  }, []);

  const remove = useCallback((slug: string) => {
    setSaved((prev) => {
      const next = prev.filter((r) => r.slug !== slug);
      write(next);
      return next;
    });
  }, []);

  const toggle = useCallback(
    (slug: string, topic = "") => {
      if (saved.some((r) => r.slug === slug)) remove(slug);
      else save(slug, topic);
    },
    [saved, save, remove],
  );

  return { saved, isSaved, save, remove, toggle };
}
